import Phaser from "phaser";
import type { Cell } from "./GridPath";
import type { TowerDef } from "../types";
import type { Enemy } from "./Enemy";
import { Projectile } from "./Projectile";
import type { JuiceSystem } from "../systems/JuiceSystem";
import { makeCaster, type CasterSprite } from "./CasterSprite";

/** Per-level multipliers + weapon-system count + armor capacity. */
const UPGRADE_MULT = [
  { dmg: 1.0, range: 1.0, rate: 1.0, costMult: 1.0, label: "I",   shots: 1, armor: 15 },
  { dmg: 1.6, range: 1.1, rate: 1.15, costMult: 0.7, label: "II",  shots: 2, armor: 30 },
  { dmg: 2.4, range: 1.2, rate: 1.35, costMult: 1.0, label: "III", shots: 3, armor: 50 },
];

export class Tower {
  readonly cell: Cell;
  readonly def: TowerDef;
  level = 1; // 1, 2, or 3
  armor: number;
  destroyed = false;
  /** Hero positions per level. Index = level index (0..2). Each entry is a list of (dx, dy) for that level's heroes. */
  private static readonly HERO_LAYOUT: Array<Array<[number, number]>> = [
    [[0, 0]],                          // lvl 1: 1 hero centered
    [[-9, 1], [9, 1]],                 // lvl 2: side-by-side
    [[-10, 4], [10, 4], [0, -7]],      // lvl 3: triangle (front pair + back archmage)
  ];
  /** Per-hero independent cooldown timers (ms). Each hero fires on its own clock. */
  private heroCooldowns: number[] = [];
  private heroes: CasterSprite[] = [];
  private platform: Phaser.GameObjects.Ellipse;
  private levelText: Phaser.GameObjects.Text;
  private armorBarBg: Phaser.GameObjects.Rectangle;
  private armorBar: Phaser.GameObjects.Rectangle;
  private rangeRing?: Phaser.GameObjects.Arc;
  private fireIdx = 0;
  /** Per-hero shot counter for throttling signature beam ultimates. */
  private shotCount = 0;
  /** Mythic / ultimate heroes that fire a Kamehameha-style beam periodically. */
  private static readonly BEAM_TOWERS = new Set(["phantom", "tinker", "luna", "voidmage", "necro"]);

  constructor(private scene: Phaser.Scene, cell: Cell, def: TowerDef) {
    this.cell = cell;
    this.def = def;
    this.armor = UPGRADE_MULT[0].armor;

    // Stone platform under the heroes
    this.platform = scene.add.ellipse(cell.x, cell.y + 14, 38, 9, 0x3a3550).setStrokeStyle(1, 0x1a1530, 1);

    // Spawn the level-1 hero.
    this.spawnHeroAtSlot(0);
    this.layoutHeroes();

    this.levelText = scene.add
      .text(cell.x + 16, cell.y - 24, "I", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "10px",
        fontStyle: "bold",
        color: "#fff066",
        stroke: "#000",
        strokeThickness: 2,
      })
      .setOrigin(0.5)
      .setVisible(false); // hidden at level 1; revealed on upgrade

    // Armor bar below the platform
    this.armorBarBg = scene.add.rectangle(cell.x, cell.y + 22, 32, 3, 0x000000, 0.6);
    this.armorBar = scene.add.rectangle(cell.x, cell.y + 22, 32, 3, 0x4cc9f0);
    this.armorBar.setOrigin(0.5, 0.5);
  }

  private spawnHeroAtSlot(slot: 0 | 1 | 2): void {
    const layout = Tower.HERO_LAYOUT[this.level - 1] ?? Tower.HERO_LAYOUT[2];
    const [dx, dy] = layout[slot] ?? [0, 0];
    const hero = makeCaster(this.scene, this.def.id, this.cell.x + dx, this.cell.y + dy, slot as 0 | 1 | 2);
    this.heroes.push(hero);
    // New hero starts ready to fire — no warm-up delay.
    this.heroCooldowns.push(0);
  }

  /** Re-position all heroes for the current level's formation. */
  private layoutHeroes(): void {
    const layout = Tower.HERO_LAYOUT[this.level - 1];
    this.heroes.forEach((h, i) => {
      const [dx, dy] = layout[i] ?? [0, 0];
      // Sort heroes back-to-front so back row is behind.
      h.setDepth(this.cell.y + dy);
      this.scene.tweens.add({
        targets: h,
        x: this.cell.x + dx,
        y: this.cell.y + dy,
        duration: 240,
        ease: "Quad.easeOut",
      });
    });
  }

  get maxArmor(): number { return UPGRADE_MULT[this.level - 1].armor; }
  get shotsPerFire(): number { return UPGRADE_MULT[this.level - 1].shots; }

  /** Damage the tower's armor. Returns true if destroyed. */
  takeDamage(dmg: number): boolean {
    if (this.destroyed) return false;
    this.armor -= dmg;
    this.updateArmorBar();
    // Brief red flash on hit
    const flash = this.scene.add.rectangle(this.cell.x, this.cell.y, 38, 38, 0xff3030, 0.5);
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 160,
      onComplete: () => flash.destroy(),
    });
    if (this.armor <= 0) {
      this.armor = 0;
      this.destroyed = true;
      this.playDeathAnim();
      return true;
    }
    return false;
  }

  private playDeathAnim(): void {
    // Smoke puff + heroes topple + fade
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const p = this.scene.add.circle(this.cell.x, this.cell.y, 4, 0x444444);
      this.scene.tweens.add({
        targets: p,
        x: this.cell.x + Math.cos(angle) * 26,
        y: this.cell.y + Math.sin(angle) * 26,
        alpha: 0,
        scale: 0.2,
        duration: 500,
        onComplete: () => p.destroy(),
      });
    }
    this.scene.tweens.add({
      targets: [...this.heroes, this.levelText, this.platform],
      alpha: 0,
      angle: 90,
      scale: 0.4,
      duration: 360,
    });
  }

  private updateArmorBar(): void {
    const ratio = Math.max(0, this.armor / this.maxArmor);
    this.armorBar.scaleX = ratio;
    if (ratio > 0.5) this.armorBar.fillColor = 0x4cc9f0;
    else if (ratio > 0.25) this.armorBar.fillColor = 0xffc23a;
    else this.armorBar.fillColor = 0xff5a3a;
  }

  /** Effective stats with level applied. */
  get damage(): number { return this.def.damage * UPGRADE_MULT[this.level - 1].dmg; }
  get range(): number { return this.def.range * UPGRADE_MULT[this.level - 1].range; }
  get rate(): number { return this.def.rate * UPGRADE_MULT[this.level - 1].rate; }

  /** Cost of upgrading to next level (or null if maxed). */
  upgradeCost(): number | null {
    if (this.level >= 3) return null;
    return Math.round(this.def.cost * UPGRADE_MULT[this.level].costMult);
  }

  upgrade(scene: Phaser.Scene): void {
    if (this.level >= 3) return;
    this.level++;
    const m = UPGRADE_MULT[this.level - 1];
    this.levelText.setText(m.label);
    this.levelText.setVisible(true);
    // Restore armor on upgrade (bonus for upgrading)
    this.armor = this.maxArmor;
    this.updateArmorBar();
    // Spawn a new hero variant for this upgrade level.
    this.spawnHeroAtSlot((this.level - 1) as 0 | 1 | 2);
    this.layoutHeroes();
    const newHero = this.heroes[this.heroes.length - 1];
    // Pop-in flourish on the new hero
    scene.tweens.add({
      targets: newHero,
      scale: { from: 0.3, to: newHero.scaleX },
      alpha: { from: 0, to: 1 },
      duration: 320,
      ease: "Back.easeOut",
    });
    const ring = scene.add.circle(this.cell.x, this.cell.y, 24, this.def.color, 0.5);
    scene.tweens.add({
      targets: ring,
      scale: 2.2,
      alpha: 0,
      duration: 360,
      ease: "Cubic.easeOut",
      onComplete: () => ring.destroy(),
    });
  }

  showRange(scene: Phaser.Scene, show: boolean): void {
    if (show && !this.rangeRing) {
      this.rangeRing = scene.add.circle(this.cell.x, this.cell.y, this.range, this.def.color, 0.08);
      this.rangeRing.setStrokeStyle(1, this.def.color, 0.5);
    } else if (!show && this.rangeRing) {
      this.rangeRing.destroy();
      this.rangeRing = undefined;
    }
  }

  update(
    deltaMs: number,
    now: number,
    enemies: Enemy[],
    projectiles: Projectile[],
    scene: Phaser.Scene,
    juice?: JuiceSystem,
  ): void {
    void now;
    if (this.destroyed) return;
    const effectiveDef: TowerDef = { ...this.def, damage: this.damage, range: this.range };
    const cooldownMs = 1000 / this.rate;

    // Each hero fires on its own independent cooldown — no shared timer, no
    // staggered delayedCall. As soon as a hero's cooldown hits 0 and an enemy
    // is in range, it fires. Two heroes side-by-side both fire as fast as the
    // tower's rate allows, in parallel.
    for (let i = 0; i < this.heroes.length; i++) {
      this.heroCooldowns[i] -= deltaMs;
      if (this.heroCooldowns[i] > 0) continue;

      // Pick best target this hero can hit (furthest along the path).
      const target = this.pickTargets(enemies, 1)[0];
      if (!target) continue;

      const hero = this.heroes[i];
      this.heroCooldowns[i] = cooldownMs;
      hero.castFlash();
      const px = hero.x + hero.castPoint.x * hero.scaleX;
      const py = hero.y + hero.castPoint.y * hero.scaleY;
      const proj = new Projectile(scene, px, py, target, effectiveDef, enemies, juice, hero.archetype);
      projectiles.push(proj);
      const sound = scene.registry.get("sound2") as { onShoot?: (colorOrId: number | string, towerId?: string) => void } | undefined;
      sound?.onShoot?.(this.def.color, this.def.id);

      // Signature ultimate: mythic heroes fire a Kamehameha-style beam every
      // few shots — a cosmetic flair from hero to target (damage still comes
      // from the projectile above, so gameplay balance is untouched).
      this.shotCount++;
      if (Tower.BEAM_TOWERS.has(this.def.id) && this.shotCount % 3 === 0) {
        const anime = scene.registry.get("anime") as
          | { beam: (x1: number, y1: number, x2: number, y2: number, c: number, w: number) => void; chargeAura: (x: number, y: number, c: number, d?: number) => void }
          | undefined;
        if (anime) {
          anime.chargeAura(px, py, this.def.color, 200);
          anime.beam(px, py, target.position.x, target.position.y, this.def.color, 13);
        }
      }
    }
    this.fireIdx = (this.fireIdx + 1) % Math.max(1, this.heroes.length);
  }

  /** Pick top N targets by furthest-along-path priority. */
  private pickTargets(enemies: Enemy[], n: number): Enemy[] {
    const inRange: Enemy[] = [];
    for (const e of enemies) {
      if (!e.alive) continue;
      const dx = e.position.x - this.cell.x;
      const dy = e.position.y - this.cell.y;
      if (Math.hypot(dx, dy) > this.range) continue;
      inRange.push(e);
    }
    inRange.sort((a, b) => b.t - a.t);
    return inRange.slice(0, n);
  }

  destroy(): void {
    this.platform.destroy();
    for (const h of this.heroes) h.destroy();
    this.levelText.destroy();
    this.armorBar.destroy();
    this.armorBarBg.destroy();
    this.rangeRing?.destroy();
  }
}
